import { ArrowLeft, Crown, Save, Scan, UserCog, Users } from 'lucide-react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { createEmployee, getPermissionList } from '../../../api/employee.api';
import styles from '../../../assets/scss/pages/EmployeeCreate.module.scss';
import { PERMISSIONS } from '../../../config/permissions';
import { usePermissions } from '../../../hooks/usePermissions';
import FormattedInput from '../../../components/common/FormattedInput';

const PERM_GROUP_LABELS = {
    customer: 'Quyền khách hàng',
    bill: 'Quyền đơn hàng và hóa đơn',
    service: 'Quyền sản phẩm',
    package: 'Gói dịch vụ',
    booking: 'Lịch hẹn - Đặt lịch',
    attribute: 'Quyền thuộc tính',
    commission: 'Quyền hoa hồng',
    feedback: 'Quyền đánh giá',
    report: 'Quyền báo cáo',
    revenue_book: 'Quyền sổ doanh thu',
    employee: 'Quyền nhân viên',
    shift_work: 'Quyền ca làm việc',
    salary: 'Quyền quản lý nhân sự',
};

const DEFAULT_EMPLOYEE_PERMS = [
    'SERVICE_VIEW_LIST', 'SERVICE_CREATE', 'SERVICE_UPDATE',
    'BILL_VIEW_LIST', 'BILL_VIEW_DETAIL', 'BILL_CREATE', 'BILL_UPDATE',
    'CUSTOMER_VIEW', 'CUSTOMER_CREATE', 'CUSTOMER_DETAIL', 'CUSTOMER_UPDATE',
    'SERVICE_VIEW_DETAIL', 'ATTRIBUTE_VIEW', 'ATTRIBUTE_CREATE',
    'COMMISSION_VIEW', 'REPORT_DASHBOARD_VIEW', 'FEEDBACK_UPDATE_QR_STATUS',
];

// Permission dependencies: { code: dependsOn }
const PERM_DEPS = {
    BILL_UPDATE: 'BILL_VIEW_DETAIL',
    SERVICE_UPDATE: 'SERVICE_VIEW_DETAIL',
    FEEDBACK_UPDATE_QR_STATUS: 'BILL_VIEW_LIST',
    EMPLOYEE_DETAIL: 'SHIFT_WORK_VIEW',
};

function MoneyInput({ value, onChange, placeholder }) {
    const inputRef = useRef(null);
    const displayValue = value ? Number(String(value).replace(/\D/g, '')).toLocaleString('vi-VN') : '';

    const handleChange = (e) => {
        const el = e.target;
        const prevVal = displayValue;
        const rawNew = el.value.replace(/\D/g, '');
        const num = rawNew ? parseInt(rawNew, 10) : 0;
        const newFormatted = num > 0 ? num.toLocaleString('vi-VN') : '';

        const cursorPos = el.selectionStart;
        const dotsBefore = (prevVal.slice(0, cursorPos).match(/\./g) || []).length;
        const rawCursorPos = cursorPos - dotsBefore;
        const dotsInNew = (pos) => {
            let count = 0, raw = 0;
            for (let i = 0; i < newFormatted.length && raw < pos; i++) {
                if (newFormatted[i] === '.') count++; else raw++;
            }
            return count;
        };
        const newCursor = rawCursorPos + dotsInNew(rawCursorPos);

        onChange(rawNew || '');
        requestAnimationFrame(() => {
            if (inputRef.current) inputRef.current.setSelectionRange(newCursor, newCursor);
        });
    };

    return (
        <input
            ref={inputRef}
            className={styles.formInput}
            value={displayValue}
            onChange={handleChange}
            placeholder={placeholder || '0'}
            inputMode="numeric"
        />
    );
}

export default function EmployeeCreate() {
    const navigate = useNavigate();
    const { hasPermission } = usePermissions();
    const canCreate = hasPermission(PERMISSIONS.EMPLOYEE.CREATE);

    // Form
    const [name, setName] = useState('');
    const [phone, setPhone] = useState('');
    const [password, setPassword] = useState('');
    const [role, setRole] = useState('employee');
    const [baseSalary, setBaseSalary] = useState('');
    const [allowance, setAllowance] = useState('');

    // Permissions
    const [permissionList, setPermissionList] = useState([]);
    const [loadingPerms, setLoadingPerms] = useState(true);
    const [selectedPerms, setSelectedPerms] = useState({});

    // UI
    const [saving, setSaving] = useState(false);
    const [errors, setErrors] = useState({});
    const [showSuccess, setShowSuccess] = useState(false);

    // Fetch permissions
    useEffect(() => {
        let mounted = true;
        const fetchPerms = async () => {
            setLoadingPerms(true);
            try {
                const { data } = await getPermissionList();
                if (mounted) {
                    setPermissionList(data.data || []);
                    // Set default permissions for employee role
                    const defaults = {};
                    DEFAULT_EMPLOYEE_PERMS.forEach((code) => { defaults[code] = true; });
                    setSelectedPerms(defaults);
                }
            } catch {
                if (mounted) setPermissionList([]);
            } finally {
                if (mounted) setLoadingPerms(false);
            }
        };
        fetchPerms();
        return () => { mounted = false; };
    }, []);

    // Format phone display
    const formatPhoneDisplay = (val) => {
        const digits = val.replace(/\D/g, '').slice(0, 10);
        if (digits.length <= 4) return digits;
        if (digits.length <= 7) return `${digits.slice(0, 4)}.${digits.slice(4)}`;
        return `${digits.slice(0, 4)}.${digits.slice(4, 7)}.${digits.slice(7)}`;
    };

    const parsePhone = (typed) => typed.replace(/\D/g, '').slice(0, 10);

    // Permission toggle with dependency handling
    const handlePermToggle = useCallback((code) => {
        setSelectedPerms((prev) => {
            const next = { ...prev };
            const isEnabled = !next[code];
            next[code] = isEnabled;

            if (isEnabled) {
                // Enabling: auto-enable dependency
                const dep = PERM_DEPS[code];
                if (dep) next[dep] = true;
            } else {
                // Disabling: check if other perms depend on this
                Object.entries(PERM_DEPS).forEach(([depCode, depOn]) => {
                    if (depOn === code && next[depCode]) {
                        next[depCode] = false;
                    }
                });
            }
            return next;
        });
    }, []);

    // Build permission codes for submit
    const buildPermissionCodes = () => {
        if (role === 'manage') return [];
        if (role === 'checkin') return ['CUSTOMER_CHECKIN', 'CUSTOMER_CREATE'];
        return Object.keys(selectedPerms).filter((code) => selectedPerms[code]);
    };

    // Validate
    const validate = () => {
        const errs = {};
        if (!name.trim()) errs.name = 'Vui lòng nhập họ tên';
        if (!phone || phone.length < 10) errs.phone = 'Số điện thoại phải đủ 10 số';
        if (!password.trim()) errs.password = 'Vui lòng nhập mật khẩu';
        if (!role) errs.role = 'Vui lòng chọn vai trò';
        return errs;
    };

    // Submit
    const handleSubmit = async () => {
        if (!canCreate) return;
        if (loadingPerms) {
            setErrors({ submit: 'Đang tải quyền, vui lòng thử lại' });
            return;
        }
        const errs = validate();
        if (Object.keys(errs).length > 0) {
            setErrors(errs);
            return;
        }

        setSaving(true);
        setErrors({});
        try {
            const payload = {
                userName: phone,
                passWord: password,
                role,
                name: name.trim(),
                phone,
                hireDate: new Date().toISOString().split('T')[0],
                permissionCodes: buildPermissionCodes(),
            };
            if (baseSalary) payload.baseSalary = Number(baseSalary);
            if (allowance) payload.allowance = Number(allowance);

            await createEmployee(payload);
            setShowSuccess(true);
            setTimeout(() => navigate('/admin/employees'), 1200);
        } catch (err) {
            const msg = err.response?.data?.message || 'Tạo nhân viên thất bại';
            setErrors({ submit: msg });
        } finally {
            setSaving(false);
        }
    };

    // Group permissions by group field
    const permGroups = {};
    permissionList.forEach((p) => {
        const g = p.group || 'other';
        if (!permGroups[g]) permGroups[g] = [];
        permGroups[g].push(p);
    });

    return (
        <div className={styles.page}>
            {/* Header */}
            <div className={styles.header}>
                <div className={styles.headerLeft}>
                    <button className={styles.backBtn} onClick={() => navigate(-1)}>
                        <ArrowLeft />
                    </button>
                    <h1 className={styles.title}>Thêm nhân viên</h1>
                </div>
                <button className={styles.saveBtn} onClick={handleSubmit} disabled={saving || !canCreate}>
                    <Save size={18} />
                    {saving ? 'Đang tạo...' : 'Tạo nhân viên'}
                </button>
            </div>

            {errors.submit && <div className={styles.errorMsg}>{errors.submit}</div>}

            <div className={styles.layout}>
                {/* ===== CỘT TRÁI ===== */}
                <div className={styles.layoutLeft}>
                    {/* Thông tin cơ bản */}
                    <div className={styles.card}>
                        <div className={styles.cardHeader}>Thông tin cơ bản</div>
                        <div className={styles.cardBody}>
                            <div className={styles.formGrid}>
                                <div className={styles.formGroup}>
                                    <label className={styles.formLabel}>Họ tên <span>*</span></label>
                                    <input
                                        className={`${styles.formInput}${errors.name ? ` ${styles.inputError}` : ''}`}
                                        value={name}
                                        onChange={(e) => { setName(e.target.value); setErrors((p) => ({ ...p, name: '' })); }}
                                        placeholder="Nhập họ tên nhân viên"
                                    />
                                    {errors.name && <span className={styles.formError}>{errors.name}</span>}
                                </div>
                                <div className={styles.formGroup}>
                                    <label className={styles.formLabel}>Số điện thoại <span>*</span></label>
                                    <FormattedInput
                                        className={`${styles.formInput}${errors.phone ? ` ${styles.inputError}` : ''}`}
                                        value={phone}
                                        format={formatPhoneDisplay}
                                        parse={parsePhone}
                                        onChange={(raw) => { setPhone(raw); setErrors((p) => ({ ...p, phone: '' })); }}
                                        placeholder="0XXX.XXX.XXX"
                                        inputMode="numeric"
                                    />
                                    {errors.phone && <span className={styles.formError}>{errors.phone}</span>}
                                    <span className={styles.formHint}>Dùng làm tên đăng nhập</span>
                                </div>
                                <div className={styles.formGroup}>
                                    <label className={styles.formLabel}>Mật khẩu <span>*</span></label>
                                    <input
                                        className={`${styles.formInput}${errors.password ? ` ${styles.inputError}` : ''}`}
                                        type="password"
                                        value={password}
                                        onChange={(e) => { setPassword(e.target.value); setErrors((p) => ({ ...p, password: '' })); }}
                                        placeholder="Nhập mật khẩu"
                                    />
                                    {errors.password && <span className={styles.formError}>{errors.password}</span>}
                                </div>
                            </div>

                            {/* Vai trò */}
                            <div className={styles.formGroup} style={{ marginTop: 24 }}>
                                <label className={styles.formLabel}>Vai trò <span>*</span></label>
                                <div className={styles.roleOptions}>
                                    <button
                                        className={`${styles.roleOption}${role === 'employee' ? ` ${styles.roleOptionActive}` : ''}`}
                                        onClick={() => setRole('employee')}
                                    >
                                        <div className={`${styles.roleIcon} ${styles.roleIconBlue}`}><Users size={20} /></div>
                                        <span className={styles.roleName}>Nhân viên</span>
                                        <span className={styles.roleDesc}>Quyền hạn tuỳ chỉnh</span>
                                    </button>
                                    <button
                                        className={`${styles.roleOption}${role === 'manage' ? ` ${styles.roleOptionActive}` : ''}`}
                                        onClick={() => setRole('manage')}
                                    >
                                        <div className={`${styles.roleIcon} ${styles.roleIconGreen}`}><Crown size={20} /></div>
                                        <span className={styles.roleName}>Quản lý</span>
                                        <span className={styles.roleDesc}>Toàn quyền như chủ</span>
                                    </button>
                                    <button
                                        className={`${styles.roleOption}${role === 'checkin' ? ` ${styles.roleOptionActive}` : ''}`}
                                        onClick={() => setRole('checkin')}
                                    >
                                        <div className={`${styles.roleIcon} ${styles.roleIconOrange}`}><Scan size={20} /></div>
                                        <span className={styles.roleName}>Check-in</span>
                                        <span className={styles.roleDesc}>Chỉ check-in khách</span>
                                    </button>
                                </div>
                                {errors.role && <span className={styles.formError}>{errors.role}</span>}
                            </div>
                        </div>
                    </div>

                    {/* Lương */}
                    <div className={styles.card}>
                        <div className={styles.cardHeader}>Thu nhập</div>
                        <div className={styles.cardBody}>
                            <div className={styles.formGrid}>
                                <div className={styles.formGroup}>
                                    <label className={styles.formLabel}>Thu nhập cơ bản</label>
                                    <MoneyInput
                                        value={baseSalary}
                                        onChange={setBaseSalary}
                                        placeholder="Tuỳ chọn"
                                    />
                                </div>
                                <div className={styles.formGroup}>
                                    <label className={styles.formLabel}>Phụ cấp</label>
                                    <MoneyInput
                                        value={allowance}
                                        onChange={setAllowance}
                                        placeholder="Tuỳ chọn"
                                    />
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* ===== CỘT PHẢI: Phân quyền ===== */}
                <div className={styles.layoutRight}>
                    <div className={styles.card}>
                        <div className={styles.cardHeader}>Phân quyền</div>
                        <div className={styles.cardBody}>
                            {role === 'manage' ? (
                                <div className={styles.permNote}>
                                    Quản lý có toàn quyền như chủ cửa hàng
                                </div>
                            ) : role === 'checkin' ? (
                                <div className={styles.permNote}>
                                    Check-in chỉ có quyền check-in và tạo khách hàng
                                </div>
                            ) : loadingPerms ? (
                                <div className={styles.permLoading}>Đang tải danh sách quyền...</div>
                            ) : (
                                Object.entries(permGroups).map(([group, perms]) => {
                                    const label = PERM_GROUP_LABELS[group] || group;
                                    return (
                                        <div key={group} className={styles.permGroup}>
                                            <div className={styles.permGroupTitle}>{label}</div>
                                            {perms.map((p) => (
                                                <label key={p.code} className={styles.permItem}>
                                                    <input
                                                        type="checkbox"
                                                        checked={!!selectedPerms[p.code]}
                                                        onChange={() => handlePermToggle(p.code)}
                                                    />
                                                    {p.name}
                                                </label>
                                            ))}
                                        </div>
                                    );
                                })
                            )}
                        </div>
                    </div>
                </div>
            </div>

            {showSuccess && (
                <div className={styles.successOverlay}>
                    <div className={styles.successModal}>
                        <div className={styles.successIcon}>✓</div>
                        <span className={styles.successText}>Tạo nhân viên thành công!</span>
                    </div>
                </div>
            )}
        </div>
    );
}
